import { LOCALE_DEFINITIONS, matchSupportedLocale } from "@/i18n/locales";

/**
 * Shared language switcher used in TopNav (public routes), LoginPage, and
 * LearnerPortalShell (authenticated header).
 *
 * On change: i18next switches and `persistLanguageChoice` writes localStorage
 * (both wired in i18n.ts). If the user accepted cookies (`rb_consent=1`),
 * `rb_lang` is also written so the marketing site can restore the preference
 * on their next visit.
 *
 * `variant` controls visual weight:
 *   "default"  — full border, brand colours (TopNav / standalone)
 *   "compact"  — borderless, icon-size (shell header, login panel header)
 */
export default function LanguageSwitcher({
  currentLang,
  onChange,
  variant = "default",
}: {
  currentLang: string;
  onChange: (lang: string) => void;
  variant?: "default" | "compact";
}) {
  const gaLocales = LOCALE_DEFINITIONS.filter((l) => l.status === "ga");
  const active = matchSupportedLocale(currentLang) ?? gaLocales[0].code;

  function handleChange(code: string) {
    try {
      const hasConsent = document.cookie
        .split(";")
        .some((c) => c.trim() === "rb_consent=1");
      if (hasConsent) {
        const exp = new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toUTCString();
        document.cookie = `rb_lang=${code}; expires=${exp}; path=/; SameSite=Lax`;
      }
    } catch (_) {
      /* storage blocked */
    }
    onChange(code);
  }

  const selectClass =
    variant === "compact"
      ? "rounded-md bg-transparent px-1.5 py-1 text-xs text-slate-600 hover:text-slate-900 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-slate-700 cursor-pointer"
      : "rounded-md border border-brand-primary/20 bg-white px-sm py-xs text-brand-primary focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-primary";

  return (
    <label className="text-sm">
      <span className="sr-only">Language</span>
      <select
        value={active}
        onChange={(e) => handleChange(e.target.value)}
        className={selectClass}
        data-testid="language-switcher"
      >
        {gaLocales.map((locale) => (
          <option key={locale.code} value={locale.code} lang={locale.code}>
            {locale.nativeLabel} · {locale.shortLabel} · {locale.englishLabel}
          </option>
        ))}
      </select>
    </label>
  );
}
